import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { DownloadIcon, StopIcon } from "@heroicons/react/outline";
import MaxWidthPage from "components/MaxWidthPage";
import Button from "components/Button";
import { CrawlerPageLayout } from "pages/crawler/components/CrawlerPageLayout";
import { RecentlyViewedProvider } from "providers/RecentlyViewed/RecentlyViewedProvider";
import JobUrlsTableSection from "pages/crawler/components/jobs/JobUrlsTableSection";
import JobStatsCard from "pages/crawler/components/jobs/JobStatsCard";
import scraperApi from "api";
import Toaster from "components/Toaster";
import { isStatusRunning, isStatusScheduled } from "pages/crawler/utils/crawlerStatusUtils";
import { useJobUrlsPagination } from "pages/crawler/hooks/useJobUrlsPagination";
import { downloadAllJobResults } from "pages/crawler/utils/crawlerDownloadUtils";
import { formatTimeAgo } from "utils/dateUtils";
import { Tooltip, PureTooltip } from "components/Tooltip";
import { CrawlerPageSkeleton } from "pages/crawler/components/jobs/StatsCardSkeleton";

// --- Helpers ---

const getStatusMessage = (stats: { urlsCrawled: number; failed: number; inProgress: number }, finishedAt?: string) => {
  const { urlsCrawled, failed, inProgress } = stats;

  if (inProgress > 0) {
    return { text: `Processing ${inProgress} URL(s)...`, color: 'text-blue-600' };
  }
  if (urlsCrawled > 0 && failed === 0) {
    return { text: `Finished crawling ${formatTimeAgo(finishedAt)}`, color: 'text-[#288a4c]' };
  }
  if (failed > 0 && urlsCrawled === 0) {
    return { text: `${failed} URL(s) failed`, color: 'text-red-600' };
  }
  if (urlsCrawled > 0 && failed > 0) {
    return { text: `Completed with ${failed} failed URL(s)`, color: 'text-orange-600' };
  }
  return { text: 'No URLs processed', color: 'text-gray-500' };
};

// --- Sub-components for title buttons ---

const StartUrlDisplay = ({ url }: { url?: string }) => (
  <Tooltip content={url || ''}>
    <p className="text-sm text-gray-900 font-normal truncate">{url}</p>
  </Tooltip>
);

const ActiveJobHeader = ({
  jobData,
  jobIsScheduled,
  onCancel,
}: {
  jobData: any;
  jobIsScheduled: boolean;
  onCancel: () => void;
}) => (
  <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
    <div className="text-left max-w-xs flex-shrink min-w-0">
      <StartUrlDisplay url={jobData?.startUrl} />
      <p className="text-sm text-gray-500">
        {jobIsScheduled
          ? `Scheduled ${formatTimeAgo(jobData?.createdAt)}`
          : `Started crawling ${formatTimeAgo(jobData?.createdAt)}`}
      </p>
    </div>
    <Button
      text="STOP"
      icon={{ Icon: StopIcon }}
      className="bg-red-600 hover:bg-red-700 text-white rounded px-4 py-2 flex-shrink-0"
      onClick={onCancel}
    />
  </div>
);

const CompletedJobHeader = ({
  jobData,
  statusMessage,
  canDownload,
  isDownloading,
  onDownload,
}: {
  jobData: any;
  statusMessage: { text: string; color: string };
  canDownload: boolean;
  isDownloading: boolean;
  onDownload: () => void;
}) => (
  <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
    <div className="text-left max-w-xs flex-shrink min-w-0">
      <p className={`text-sm font-medium ${statusMessage.color}`}>
        {statusMessage.text}
      </p>
      <StartUrlDisplay url={jobData?.startUrl} />
    </div>
    <PureTooltip content="Downloads all results as a ZIP file">
      <Button
        text={isDownloading ? "DOWNLOADING..." : "DOWNLOAD ALL"}
        icon={{ Icon: DownloadIcon }}
        disabled={!canDownload || isDownloading}
        onClick={canDownload ? onDownload : undefined}
        className="button button-primary px-8 flex-shrink-0"
      />
    </PureTooltip>
  </div>
);

// --- Page component ---

const ViewAllCrawlerJobScrapesPage = () => {
  const { id: crawlerId, jobId } = useParams();
  const [isDownloading, setIsDownloading] = useState(false);
  const [crawlerName, setCrawlerName] = useState<string | null>(null);
  const {
    urls,
    pagination,
    jobData,
    isLoading,
    error,
    searchInput,
    setSearchInput,
    setPage,
    statusFilter,
    setStatusFilter,
    refetch,
  } = useJobUrlsPagination(jobId);

  // Fetch crawler name once (separate endpoint, doesn't change)
  useEffect(() => {
    if (!crawlerId) return;
    let cancelled = false;

    scraperApi.crawlers.getCrawlerById(crawlerId)
      .then((response: any) => {
        if (!cancelled && response.success && response.data) {
          setCrawlerName(response.data.name || null);
        }
      })
      .catch(() => {});

    return () => { cancelled = true; };
  }, [crawlerId]);

  // Derived state
  const jobIsRunning = jobData?.status ? isStatusRunning(jobData.status) : false;
  const jobIsScheduled = jobData?.status ? isStatusScheduled(jobData.status) : false;
  const jobIsActive = jobIsRunning || jobIsScheduled;

  const stats = jobData?.stats;
  const completedUrls = stats?.urlsCrawled ?? 0;
  const failedUrls = stats?.failed ?? 0;
  const inProgressUrls = stats?.inProgress ?? 0;
  const canDownload = !jobIsActive && completedUrls > 0;

  const displayName = crawlerName || (jobData?.id ? `Job ${jobData.id.slice(0, 8)}` : jobId || '');
  const statusMessage = getStatusMessage({ urlsCrawled: completedUrls, failed: failedUrls, inProgress: inProgressUrls }, jobData?.finishedAt);

  // Handlers
  const handleCancelJob = async () => {
    if (!jobId) return;
    try {
      const response = await scraperApi.crawlers.cancelCrawlerJob(jobId);
      if (response.success) {
        Toaster.success('Job cancelled successfully');
        refetch();
      } else {
        throw new Error(response.message || 'Failed to cancel job');
      }
    } catch (err: any) {
      console.error('Failed to cancel job:', err);
      Toaster.error(err.response?.data?.message || err.message || 'Failed to cancel job');
    }
  };

  const handleDownloadAll = async () => {
    const actualJobId = jobData?.id || jobId;
    if (!actualJobId) return;

    setIsDownloading(true);
    try {
      const outputFormat = jobData?.outputFormat?.toLowerCase() || 'json';
      await downloadAllJobResults(actualJobId, outputFormat);
    } finally {
      setIsDownloading(false);
    }
  };

  // Initial loading skeleton
  if (isLoading && !jobData) {
    return (
      <MaxWidthPage fullWidth>
        <CrawlerPageSkeleton />
      </MaxWidthPage>
    );
  }

  return (
    <MaxWidthPage fullWidth>
      <RecentlyViewedProvider>
        <CrawlerPageLayout
          crawlerName={`${displayName} / ${jobId}`}
          readonly={true}
          titleButtons={
            jobIsActive ? (
              <ActiveJobHeader
                jobData={jobData}
                jobIsScheduled={jobIsScheduled}
                onCancel={handleCancelJob}
              />
            ) : (
              <CompletedJobHeader
                jobData={jobData}
                statusMessage={statusMessage}
                canDownload={canDownload}
                isDownloading={isDownloading}
                onDownload={handleDownloadAll}
              />
            )
          }
        >
          <div className="mb-6 min-h-[60vh]">
            {jobData && (
              <div className="mb-6">
                <JobStatsCard details={jobData} />
              </div>
            )}
            <JobUrlsTableSection
              urls={urls}
              pagination={pagination}
              isLoading={isLoading}
              error={error}
              searchInput={searchInput}
              setSearchInput={setSearchInput}
              setPage={setPage}
              statusFilter={statusFilter}
              setStatusFilter={setStatusFilter}
              jobId={jobData?.id}
            />
          </div>
        </CrawlerPageLayout>
      </RecentlyViewedProvider>
    </MaxWidthPage>
  );
};

export default ViewAllCrawlerJobScrapesPage;
